import { Component, OnInit, OnDestroy, AfterViewChecked, Input, ViewChild, ElementRef } from '@angular/core';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { CellClickEvent, DataStateChangeEvent, GridDataResult, SelectableSettings } from '@progress/kendo-angular-grid';
import { MessageService } from '../../core/service/message.service';
import { NGXLogger } from 'ngx-logger';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Message } from '../../case/model/message';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { TranslateService } from '@ngx-translate/core';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { MessageListService } from 'src/app/core/service/grid-data/grid-data-message.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { UntypedFormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { FhirService } from 'src/app/core/service/fhir.service';

@Component({
  standalone: false,
  selector: 'app-message-list',
  templateUrl: './message-list.component.html',
  styleUrls: ['./message-list.component.scss']
})
export class MessageListComponent implements OnInit, OnDestroy, AfterViewChecked
{
  @Input() public dashboard: boolean;
  @ViewChild('splitter', { read: ElementRef }) public splitter: ElementRef;

  public pageSizes = [5, 10, 25, 50, 100];
  public enableAdvanced = false;
  public breadcrumb = '';
  public selectedMessages: number[] = [];
  public selected: Message = null;
  public messageDialogOpened = false;
  public filterText = '';
  public confirmEraseDialogOpened = false;
  public patientLabel = {};
  /* PR: Disabling hard delete (allowHardDelete=false) because we lose traceability.
         If we want to keep delete, we need a to implement a second level of soft delete.*/
  public allowHardDelete = false;
  public savingMessage = false;
  public isMemberAndCaseLinkEnabled = false;
  public fhirEmbedded = false;
  public fhirFullSite = false;
  public state: State = {
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    }
  };
  public messages = [];
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public searchControl = new UntypedFormControl();

  /**
   * Subscription to Angular component route parameters.
   */
  private routeParamSubscription: Subscription;
  private messageCountSubscription: Subscription;
  private onText = '';

  constructor(
    private authService: AuthenticationService,
    private messageService: MessageService,
    private messageListService: MessageListService,
    private route: ActivatedRoute,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private messengerService: MessengerService,
    private searchHelperService: SearchHelperService,
    private settingService: SettingService,
    private fhirService: FhirService,
    public intl: IntlService) { }

  public get loading(): boolean
  {
    return this.messageListService.loading;
  }

  /**
   * Bulk selection uses checkboxes only (no Ctrl/Shift). Sent folder has no checkbox column — single row selection.
   */
  public get messageGridSelectable(): SelectableSettings
  {
    return this.breadcrumb === 'SENT'
      ? { mode: 'single' }
      : { mode: 'multiple', checkboxOnly: true };
  }

  public ngOnInit(): void
  {
    this.fhirEmbedded = this.fhirService.embedded;
    this.fhirFullSite = this.fhirService.fullSite;
    this.isMemberAndCaseLinkEnabled = this.authService.currentUser.role !== 'ROLE_REFERRAL_COORDINATOR' &&
      this.authService.currentUser.role !== 'ROLE_DIRECTORY_MANAGER' &&
      this.authService.currentUser.role !== 'ROLE_DATA_ANALYST';

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    this.translate.get('ON').subscribe({ next: (value) => { this.onText = value; } });

    this.state = {
      filter: {
        filters: [], logic: 'and'
      }, group: [], skip: 0,
      sort: [
        { field: 'sentOn', dir: 'desc' }
      ],
      take: this.appConfigService.getConfig().pageSize
    };

    this.routeParamSubscription = this.route.params.subscribe({
      next: (params) =>
      {
        if (params.filter === 'inbox' || this.dashboard)
        {
          this.breadcrumb = 'INBOX';
        }
        else if (params.filter === 'deleted' || params.filter === 'sent')
        {
          this.breadcrumb = params.filter.toUpperCase();
        }
        else
        {
          this.breadcrumb = 'ALL';
        }
        this.getMessages(params.filter, true);
      }
    });

    this.messageCountSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'wsMessagesCount' && this.breadcrumb === 'INBOX')
        {
          this.getMessages(this.breadcrumb);
        }
      }
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(500))
      .subscribe({
        next: (searchValue: string) =>
        {
          this.searchFieldChange(searchValue);
        }
      });
  }

  public ngAfterViewChecked(): void
  {
    this.updateSplitterBar();
  }

  public ngOnDestroy(): void
  {
    if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
    if (this.messageCountSubscription !== undefined) { this.messageCountSubscription.unsubscribe(); }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.getMessages(this.breadcrumb);
  }

  /**
   * Grid selection changed (keys are message ids).
   */
  public onSelectedKeysChange(event: any[]): void
  {
    this.selectedMessages = this.normalizeSelectionKeys(event);
    if (this.selectedMessages.length === 1)
    {
      const row = this.findMessageById(this.selectedMessages[0]);
      this.selected = row ?? null;
      if (row)
      {
        this.markMessageReadIfNeeded(row);
      }
    }
    else
    {
      this.messageDialogOpened = false;
      this.selected = null;
    }
  }

  /**
   * With checkboxOnly, row clicks do not change selection. A click on a data cell (not column 0
   * checkbox column, not links) selects that row only and opens the detail pane. Use checkboxes to select multiple rows for bulk actions.
   */
  public onGridCellClick(event: CellClickEvent): void
  {
    if (this.breadcrumb === 'SENT')
    {
      return;
    }
    if (event.columnIndex === 0)
    {
      return;
    }
    const oe = event.originalEvent;
    if (oe?.target instanceof HTMLElement)
    {
      if (oe.target.closest('a[routerLink], a[href]'))
      {
        return;
      }
    }
    const row = event.dataItem as Message;
    if (!row?.id)
    {
      return;
    }
    this.selectedMessages = [row.id];
    this.selected = row;
    this.messageDialogOpened = false;
    this.markMessageReadIfNeeded(row);
  }

  /*
  * Open new message dialog
  */
  public newMessage(): void
  {
    this.savingMessage = false;
    this.messageDialogOpened = true;
  }

  public closeNewMessage(): void
  {
    this.messageDialogOpened = false;
    this.selectedMessages = [];
    this.selected = null;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.getMessages(this.breadcrumb, true);
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state, 'subject');
    this.filterText = filterText;
    this.filterChange(root);
  }

  public deleteMessages(): void
  {
    const messages = this.getMessagesFromSelection();
    if (messages.length > 0)
    {
      this.messageService.deleteMessages(messages).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('DELETE-MESSAGE-SUCCESSFULLY');
          this.getMessages(this.breadcrumb, true);
        }
      });
    }
  }

  public unreadMessages(): void
  {
    const messages = this.getMessagesFromSelection();
    if (messages.length > 0)
    {
      messages.forEach((m) => { m.status = 'Unread'; });

      this.messageService.updateMessages(messages).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('UPDATE-MESSAGE-SUCCESSFULLY');
          this.getMessages(this.breadcrumb, true);
        }
      });
    }
  }

  public readMessages(): void
  {
    const messages = this.getMessagesFromSelection();
    if (messages.length > 0)
    {
      messages.forEach((m) => { m.status = 'Read'; });

      this.messageService.updateMessages(messages).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('UPDATE-MESSAGE-SUCCESSFULLY');
          this.getMessages(this.breadcrumb, true);
        }
      });
    }
  }

  public undeleteMessages(): void
  {
    const messages = this.getMessagesFromSelection();
    if (messages.length > 0)
    {
      this.messageService.undeleteMessages(messages).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('UPDATE-MESSAGE-SUCCESSFULLY');
          this.getMessages(this.breadcrumb, true);
        }
      });
    }
  }

  public eraseMessages(): void
  {
    const messages = this.getMessagesFromSelection();
    if (messages.length > 0)
    {
      this.messageService.eraseMessages(messages).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('DELETE-MESSAGE-SUCCESSFULLY');
          this.getMessages(this.breadcrumb, true);
        }
      });
    }
  }

  public pageChange(): void
  {
    this.selectedMessages = [];
    this.selected = null;
  }

  /**
   * When messages in embedded and have a patient context, new message will use that member id for the compose.
   *
   * @returns Numeric Channels memberId or null.
   */
  public getNewMessageMemberId(): number
  {
    return (this.fhirService.embedded && !this.fhirService.fullSite && this.fhirService.patientContext) ? this.fhirService.patientContext?.id : undefined;
  }

  public updateSplitterBar()
  {
    const bar = this.splitter.nativeElement.querySelector('.k-splitbar');

    if (bar)
    {
      const pane = this.splitter.nativeElement.querySelector('.k-pane');
      const size = pane ? pane.offsetHeight : 0;

      bar.setAttribute('aria-valuenow', size.toString());
      bar.setAttribute('aria-valuemax', '9999');
    }
  }

  private getMessages(filter: string, resetSelection = false): void
  {
    // Optionally reset selection
    if (resetSelection)
    {
      this.selectedMessages = [];
      this.selected = null;
      this.state.skip = 0;
    }

    this.messageListService.queryByState(this.state, (messages: any) =>
    {
      if (messages !== null)
      {
        messages.data.forEach(d =>
        {
          d.sentOn = new Date(d.sentOn);
          d.toAddresses = d.to.map(x => x.name).join();
          d.from = d.fromDirectAddress ? d.fromDirectAddress : d.from;
          if ((d.body !== null) && (d.body.indexOf('\n\n\n' + this.onText) > -1))
          {
            d.body = d.body.substring(0, d.body.indexOf('\n\n\n' + this.onText));
          }
        });
        this.messages = messages.data;
        this.gridView = {
          data: (messages) ? this.gridData = messages.data : this.gridData = [],
          total: (messages) ? this.total = messages.total : this.total = 0
        };
      }
    }, `&status=${filter}`);
  }

  private markMessageReadIfNeeded(row: Message): void
  {
    if (this.breadcrumb === 'SENT' || row.status === 'Read')
    {
      return;
    }
    row.status = 'Read';
    this.messageService.updateMessages([row]).subscribe({
      error: (error) =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError(error);
      }
    });
  }

  private normalizeSelectionKeys(keys: any[]): number[]
  {
    if (!keys?.length)
    {
      return [];
    }
    return keys.map((k) => (typeof k === 'string' ? Number(k) : k)).filter((k) => k != null && !Number.isNaN(Number(k)));
  }

  private findMessageById(id: number): Message | undefined
  {
    return this.gridView?.data?.find((m: Message) => Number(m.id) === Number(id));
  }

  private getMessagesFromSelection(): Message[]
  {
    if (!this.gridView?.data?.length || !this.selectedMessages.length)
    {
      return [];
    }
    const idSet = new Set(this.selectedMessages.map((k) => Number(k)));
    return this.gridView.data.filter((m: Message) => idSet.has(Number(m.id)));
  }
}
